/**
 * G5 / V25: easy project setup that learns the user's tendencies, and agent-led initialization.
 *
 * - **Tendencies** (`setting` entity `tendencies`, sealed): defaults learned from every checkout
 *   the user's machines report (shared config, instruction files, branch naming, dev dir, lanes,
 *   secrets conventions), each with the evidence that produced it. Derived on a trusted device.
 * - **Tendency facts** (inside the encrypted `machine.projects` report): the small per-repo facts
 *   a device cannot read from the committed config (instruction files, task dir, branch prefixes).
 * - **Scoping** (`project.scope`, device-signed): a discuss-first conversation with the triage
 *   agent in a scratch workspace on the chosen machine, before any checkout exists. The agent
 *   states outcomes as `Goal:` / `Milestone:` / `Lane:` / `Task:` / `Config:` lines; the device
 *   parses them deterministically (`deriveSetupOutcome`) into reviewable proposal cards.
 *
 * Everything here travels inside E2E-encrypted entities, reports or command payloads. The hub
 * sees ids, kinds and ciphertext only.
 */
import { z } from 'zod';
import { CheckoutDirName, ProjectCreateTarget } from './account-projects.js';
import { PrivateEntityId } from './encrypted-entities.js';
const Id = PrivateEntityId;
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const Line = (max) => z.string().trim().min(1).max(max);
// ---- tendencies ---------------------------------------------------------------------------------
export const TENDENCY_KEYS = [
    'devRoot',
    'targetBranch',
    'branchPrefixes',
    'loopProfile',
    'autonomy',
    'mergeMode',
    'defaultAgent',
    'worktreeStrategy',
    'packageManager',
    'testRecipe',
    'buildRecipe',
    'releaseLanes',
    'secrets',
    'instructionFiles',
    'instructionStyle',
    'taskDir',
    'stack',
];
export const TendencyKey = z.enum(TENDENCY_KEYS);
/** Shared-config paths a tendency (or the scoping agent) may set on a drafted v2 config. */
export const SETUP_CONFIG_KEYS = {
    loopProfile: z.enum(['fast', 'balanced', 'careful']),
    'autonomy.level': z.enum(['careful', 'balanced', 'high']),
    'landing.mergeMode': z.enum(['ff', 'merge', 'squash', 'pr']),
    'landing.targetBranch': z
        .string()
        .min(1)
        .max(100)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._/-]*$/),
    'landing.test': z.string().trim().min(1).max(500),
    'landing.build': z.string().trim().min(1).max(500),
    'routing.defaultAgent': z.enum(['claude', 'codex']),
    'worktree.strategy': z.enum(['worktree', 'pool', 'single-lane']),
};
export const SETUP_CONFIG_KEY_LIST = Object.keys(SETUP_CONFIG_KEYS);
export function isSetupConfigKey(k) {
    return Object.hasOwn(SETUP_CONFIG_KEYS, k);
}
export const Tendency = z.strictObject({
    key: TendencyKey,
    /** Short human label, e.g. "Loop profile". */
    label: Line(80),
    /** What the user tends to do, human-readable ("fast", "pnpm", "CLAUDE.md + AGENTS.md"). */
    value: Line(300),
    /** Applied to a new project's drafted shared config (only for SETUP_CONFIG_KEYS). */
    apply: z
        .strictObject({ path: z.enum(SETUP_CONFIG_KEY_LIST), value: Line(500) })
        .optional(),
    /** How many of the user's projects show it, out of how many that could. */
    support: z.number().int().nonnegative().max(10_000),
    of: z.number().int().nonnegative().max(10_000),
    /** strong: a clear majority of at least two projects; weak: a lean worth offering, not applying. */
    strength: z.enum(['strong', 'weak']),
    /** Why Viko believes it ("4 of 5 projects use pnpm test"). */
    explanation: Line(500),
    /** Project names that show it (drillable back to the project). */
    examples: z.array(Line(256)).max(8),
});
/** `setting` entity value for name `tendencies`. */
export const ProjectTendencies = z.strictObject({
    v: z.literal(1),
    derivedAt: Ts,
    /** Checkouts considered, and on how many machines. */
    projects: z.number().int().nonnegative().max(10_000),
    machines: z.number().int().nonnegative().max(64),
    items: z.array(Tendency).max(64),
});
/** Per-repo facts the committed config cannot tell (reported encrypted by the machine). */
export const TendencyFacts = z.strictObject({
    /** The local project id (`project.id` in viko.project.yaml) or the folder name. */
    localId: Line(256),
    name: Line(256),
    /** Account project this checkout belongs to, when bound. */
    projectId: Id.optional(),
    /** Root instruction files present. */
    instructionFiles: z
        .array(z.enum(['CLAUDE.md', 'AGENTS.md', 'CLAUDE.local.md', 'AGENTS.override.md']))
        .max(4),
    /** CLAUDE.md is a pointer to AGENTS.md (`@AGENTS.md`), or the reverse. */
    instructionShape: z.enum(['pointer', 'duplicate', 'divergent', 'single', 'none']),
    /** Top-level section headings of the root CLAUDE.md (or AGENTS.md), in order. */
    headings: z.array(Line(120)).max(16),
    /** Task-file directories (repo-relative), e.g. `.claude/tasks`. */
    taskDirs: z.array(Line(256)).max(8),
    /** Local branch-name prefixes with counts, e.g. `feature/` × 6. */
    branchPrefixes: z
        .array(z.strictObject({ prefix: Line(64), count: z.number().int().min(1) }))
        .max(8),
    /** Lockfile-derived package manager, when any. */
    packageManager: z.enum(['pnpm', 'npm', 'yarn', 'bun', 'cargo', 'swiftpm', 'gradle']).optional(),
});
// ---- scoping ------------------------------------------------------------------------------------
export const SETUP_BRIEF_LINES_MAX = 40;
/** `project.scope`: open a scoping workspace for a project that has no checkout here yet. */
export const ProjectScopePayload = z.strictObject({
    projectId: Id,
    name: Line(256),
    goal: z.string().max(4000).optional(),
    repo: z.discriminatedUnion('kind', [
        z.strictObject({ kind: z.literal('create'), target: ProjectCreateTarget }),
        z.strictObject({ kind: z.literal('existing'), remote: Line(2048) }),
    ]),
    dirName: CheckoutDirName,
    /** The account-wide tendencies as short explained lines (the agent's context). */
    tendencies: z.array(Line(300)).max(SETUP_BRIEF_LINES_MAX).optional(),
});
/**
 * An audit finding for an existing repo, proposed for review. Only repo files are ever named;
 * user-owned global files (~/.claude, ~/.codex, ~/AGENTS.md…) are reported as notes, never edited.
 */
export const SetupFinding = z.strictObject({
    id: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9:_-]+$/),
    title: Line(200),
    detail: z.string().max(2000),
    /** Repo-relative paths the change would touch (never absolute, never outside the repo). */
    paths: z
        .array(z
        .string()
        .min(1)
        .max(512)
        .refine((p) => !p.startsWith('/') &&
        !p.startsWith('~') &&
        !/^[A-Za-z]:/.test(p) &&
        !p.split(/[\\/]/).includes('..'), 'repo-relative path'))
        .max(16),
    /** `task`: an agent does it through a normal verified landing; `migrate-config`: the v2 card. */
    via: z.enum(['task', 'migrate-config']),
    /** A preview of the proposed edit (unified diff), clipped. */
    patch: z.string().max(20_000).optional(),
});
export const ProjectScopeResult = z.strictObject({
    status: z.enum(['ready', 'failed']),
    /** `clone`: an existing clone on this machine was audited (read-only). */
    workspace: z.enum(['scratch', 'clone']).optional(),
    /** Drafted v2 shared config (audit + this machine's learned defaults). */
    draftConfig: z.string().max(64_000).optional(),
    /** The repo already has a committed `viko.project.yaml` (version). */
    existingConfig: z.union([z.literal(1), z.literal(2)]).optional(),
    /** What the audit learned about an existing repo, one fact per line. */
    facts: z.array(Line(300)).max(24).optional(),
    findings: z.array(SetupFinding).max(16).optional(),
    /** Notes about user-owned global files: shown, never changed. */
    globalNotes: z.array(Line(500)).max(8).optional(),
    reason: z.string().max(1000).optional(),
});
export const PROJECT_SETUP_COMMAND_PAYLOADS = {
    /**
     * Device-signed, not risky: it clones and creates nothing, runs no project code, and the
     * session it enables uses the read-only triage profile in a scratch folder on this machine.
     */
    'project.scope': ProjectScopePayload,
};
/** The project entity's record of how it was set up (links the saved scoping conversation). */
export const ProjectSetupState = z.strictObject({
    state: z.enum(['scoping', 'created']),
    /** The scoping conversation (a sealed `bark` entity of this project). */
    barkId: Id.optional(),
    machineId: z.string().min(1).max(128),
    startedAt: Ts,
    createdAt: Ts.optional(),
    /** Accepted card ids, for the record. */
    accepted: z.array(z.string().min(1).max(128)).max(128).optional(),
    /** The repository choice and the machine's scope result, so a scoping session can resume. */
    repo: ProjectScopePayload.shape.repo.optional(),
    dirName: CheckoutDirName.optional(),
    scope: ProjectScopeResult.optional(),
});
// ---- outcomes → proposals -----------------------------------------------------------------------
/** What the scoping preamble asks the agent to write; the parser below reads exactly this. */
export const SCOPING_OUTCOME_FORMAT = [
    'When the scope settles (and whenever it changes), restate the whole proposal as plain lines:',
    '"Goal: <one sentence>"',
    '"Milestone: <title>" followed by "- <checkable criterion>" lines',
    '"Lane: <workstream name> — <what it covers>"',
    '"Task [<lane name>]: <first task title>" (one per task; the lane is optional)',
    `"Config: <key> = <value>" only to change a default; keys: ${Object.keys(SETUP_CONFIG_KEYS).join(', ')}`,
].join('\n');
const clean = (line) => line
    .trim()
    .replace(/^(?:[-*+]|\d+[.)])\s+/, '')
    .replace(/\*\*|__/g, '')
    .trim();
const GOAL = /^goal\s*[:：]\s*(.+)$/i;
const MILESTONE = /^milestone(?:\s+\d+)?\s*[:：]\s*(.+)$/i;
const LANE = /^lane\s*[:：]\s*(.+)$/i;
const TASK = /^task(?:\s*\[([^\]]{1,120})\])?\s*[:：]\s*(.+)$/i;
const CONFIG = /^config\s*[:：]\s*([A-Za-z.]+)\s*=\s*(.+)$/i;
const isMarker = (l) => [GOAL, MILESTONE, LANE, TASK, CONFIG].some((r) => r.test(l));
const norm = (s) => s.toLowerCase().replace(/\s+/g, ' ').trim();
/**
 * Parse the scoping conversation into a proposal. The LATEST statement wins: an agent that
 * restates the scope replaces the earlier milestones/lanes/tasks set (so revisions don't pile up),
 * while a user line (`Task: …` typed by the user) adds to it.
 */
export function deriveSetupOutcome(b) {
    const out = { milestones: [], lanes: [], tasks: [], config: [] };
    for (const m of b.thread) {
        if (m.author === 'system')
            continue;
        const lines = m.text.split('\n');
        const fresh = { milestones: [], lanes: [], tasks: [], config: [] };
        let goal;
        for (let i = 0; i < lines.length; i++) {
            const line = clean(lines[i]);
            const ev = { messageId: m.id, quote: line.slice(0, 500) };
            const g = GOAL.exec(line);
            const ms = g ? null : MILESTONE.exec(line);
            const ln = g || ms ? null : LANE.exec(line);
            const tk = g || ms || ln ? null : TASK.exec(line);
            const cf = g || ms || ln || tk ? null : CONFIG.exec(line);
            if (g)
                goal = { text: g[1].trim().slice(0, 4000), evidence: ev };
            else if (ms) {
                const criteria = [];
                while (i + 1 < lines.length && lines[i + 1].trim() && !isMarker(clean(lines[i + 1])))
                    criteria.push(clean(lines[++i]).slice(0, 300));
                fresh.milestones.push({
                    title: ms[1].trim().slice(0, 200),
                    criteria: criteria.filter(Boolean).slice(0, 24),
                    evidence: ev,
                });
            }
            else if (ln) {
                const [name, ...rest] = ln[1].split(/\s+[—–-]\s+|\s*:\s+/);
                const brief = rest.join(' — ').trim();
                fresh.lanes.push({
                    name: name.trim().slice(0, 200),
                    ...(brief ? { brief: brief.slice(0, 2000) } : {}),
                    evidence: ev,
                });
            }
            else if (tk) {
                fresh.tasks.push({
                    title: tk[2].trim().slice(0, 200),
                    ...(tk[1]?.trim() ? { lane: tk[1].trim() } : {}),
                    evidence: ev,
                });
            }
            else if (cf) {
                const key = cf[1].trim();
                const value = cf[2].trim().replace(/^[`"']|[`"']$/g, '');
                if (isSetupConfigKey(key) && SETUP_CONFIG_KEYS[key].safeParse(value).success)
                    fresh.config.push({ key, value, evidence: ev });
            }
        }
        if (goal)
            out.goal = goal;
        const replace = m.author === 'agent';
        if (fresh.milestones.length)
            out.milestones = replace ? fresh.milestones : [...out.milestones, ...fresh.milestones];
        if (fresh.lanes.length)
            out.lanes = replace ? fresh.lanes : [...out.lanes, ...fresh.lanes];
        if (fresh.tasks.length)
            out.tasks = replace ? fresh.tasks : [...out.tasks, ...fresh.tasks];
        for (const c of fresh.config)
            out.config = [...out.config.filter((x) => x.key !== c.key), c];
    }
    const dedupe = (xs, key) => {
        const seen = new Set();
        return xs.filter((x) => {
            const k = norm(key(x));
            if (seen.has(k))
                return false;
            seen.add(k);
            return true;
        });
    };
    out.milestones = dedupe(out.milestones, (x) => x.title).slice(0, 16);
    out.lanes = dedupe(out.lanes, (x) => x.name).slice(0, 16);
    out.tasks = dedupe(out.tasks, (x) => x.title).slice(0, 32);
    return out;
}
//# sourceMappingURL=project-setup.js.map